import React, { useState } from 'react'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { format, formatDistanceToNow } from 'date-fns'
import { cn } from '@/lib/utils'
import { ResponseDetailView } from '@/components/responses/ResponseDetailView'
import {
	HoverCard,
	HoverCardContent,
	HoverCardTrigger,
} from '@/components/ui/hover-card'
import { Monitor, Smartphone, Globe, Star } from 'lucide-react'

// Define UserMetadata type locally or import from a shared location
interface UserMetadata {
	device: string | null
	browser: string | null
	location: string | null
	pageUrl?: string | null
	language?: string | null
	referrer?: string | null
	timezone?: string | null
	pageTitle?: string | null
	timestamp?: string | null
	userAgent?: string | null
	screenSize?: {
		width: number
		height: number
	} | null
}

// Copy DeviceInfo component here
const DeviceInfo = ({ metadata }: { metadata?: UserMetadata | null }) => {
	// Explicitly return null if metadata is missing
	if (!metadata) {
		return null
	}

	// Check if there's any meaningful data to display before rendering HoverCard
	if (
		!metadata.device &&
		!metadata.browser &&
		!metadata.location &&
		!metadata.pageTitle &&
		!metadata.timezone &&
		!metadata.screenSize
	) {
		return null
	}

	return (
		<HoverCard>
			<HoverCardTrigger asChild>
				<div className="flex items-center gap-1 cursor-help justify-end">
					{metadata.device?.toLowerCase().includes('mobile') ? (
						<Smartphone className="h-3.5 w-3.5 text-muted-foreground" />
					) : metadata.device ? (
						<Monitor className="h-3.5 w-3.5 text-muted-foreground" />
					) : null}
					{metadata.location && (
						<Globe className="h-3.5 w-3.5 text-muted-foreground" />
					)}
				</div>
			</HoverCardTrigger>
			<HoverCardContent className="w-80">
				<div className="space-y-2">
					{metadata.device && (
						<div className="flex items-center gap-2">
							<span className="text-sm font-medium">Device:</span>
							<span className="text-sm text-muted-foreground">
								{metadata.device}
							</span>
						</div>
					)}
					{metadata.browser && (
						<div className="flex items-center gap-2">
							<span className="text-sm font-medium">Browser:</span>
							<span className="text-sm text-muted-foreground capitalize">
								{metadata.browser}
							</span>
						</div>
					)}
					{metadata.location && (
						<div className="flex items-center gap-2">
							<span className="text-sm font-medium">Location:</span>
							<span className="text-sm text-muted-foreground">
								{metadata.location}
							</span>
						</div>
					)}
					{metadata.pageTitle && (
						<div className="flex items-center gap-2">
							<span className="text-sm font-medium">Page:</span>
							<span className="text-sm text-muted-foreground truncate max-w-[200px]">
								{metadata.pageTitle}
							</span>
						</div>
					)}
					{metadata.timezone && (
						<div className="flex items-center gap-2">
							<span className="text-sm font-medium">Timezone:</span>
							<span className="text-sm text-muted-foreground">
								{metadata.timezone}
							</span>
						</div>
					)}
					{metadata.screenSize && (
						<div className="flex items-center gap-2">
							<span className="text-sm font-medium">Screen:</span>
							<span className="text-sm text-muted-foreground">
								{metadata.screenSize.width}×{metadata.screenSize.height}
							</span>
						</div>
					)}
				</div>
			</HoverCardContent>
		</HoverCard>
	)
}

interface Response {
	id: string
	created_at: string
	program_id: string | null
	score: number | null
	response_data: any
	user_metadata: UserMetadata
	widgets: {
		name: string
		type: string
		question: string
	}
}

interface RecentActivityResponseTableProps {
	responses?: Response[]
	isLoading?: boolean
	className?: string
}

const getNPSCategory = (score: number) => {
	if (score >= 9) return 'Promoter'
	if (score >= 7) return 'Passive'
	return 'Detractor'
}

const getNPSBadgeColor = (score: number) => {
	if (score >= 9) return 'bg-green-500'
	if (score >= 7) return 'bg-yellow-500'
	return 'bg-red-500'
}

const getCSATLabel = (score: number) => {
	const labels = [
		'Very Unsatisfied',
		'Unsatisfied',
		'Neutral',
		'Satisfied',
		'Very Satisfied',
	]
	return labels[score - 1] || 'Unknown'
}

const ResponseSummary = ({ response }: { response: Response }) => {
	const { score, response_data, widgets } = response

	if (widgets.type === 'nps' && score !== null) {
		return (
			<Badge className={cn('font-medium text-white border-0 shadow-sm', getNPSBadgeColor(score))}>
				{getNPSCategory(score)} ({score}/10)
			</Badge>
		)
	}
	if (widgets.type === 'csat' && score !== null) {
		return (
			<Badge className="bg-blue-100 text-blue-800 border-blue-200 font-medium shadow-sm">
				{getCSATLabel(score)} ({score}/5)
			</Badge>
		)
	}

	// For custom or other types, show the first answer if available
	if (response_data?.customAnswers) {
		const answers = Object.values(response_data.customAnswers)
		if (answers.length > 0) {
			const firstAnswer = answers[0] as { answer: string; questionText: string }
			return (
				<div className="space-y-1.5">
					<p className="text-xs text-gray-500 dark:text-gray-400 font-medium">
						{firstAnswer.questionText}
					</p>
					<p className="text-sm text-gray-800 dark:text-gray-200 truncate max-w-[280px]">
						{firstAnswer.answer}
					</p>
				</div>
			)
		}
	}

	// For testimonials/reviews
	if (
		(widgets.type === 'testimonial' || widgets.type === 'review') &&
		response_data?.testimonialText
	) {
		return (
			<p className="text-sm text-gray-700 dark:text-gray-300 truncate max-w-[280px] italic">
				"{response_data.testimonialText}"
			</p>
		)
	}

	return (
		<span className="text-sm text-gray-500 dark:text-gray-400 italic">
			No response data
		</span>
	)
}

const RecentActivityResponseTable: React.FC<
	RecentActivityResponseTableProps
> = ({ responses = [], isLoading, className }) => {
	const [selectedResponseId, setSelectedResponseId] = useState<string | null>(
		null
	)

	if (isLoading) {
		return (
			<div className="p-8 text-center">
				<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
				<p className="text-sm text-gray-600 dark:text-gray-400">Loading responses...</p>
			</div>
		)
	}

	if (!responses.length) {
		return (
			<div className="p-8 text-center">
				<div className="w-12 h-12 mx-auto mb-4 text-gray-400">
					<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-full h-full">
						<path d="M9 11H5a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2h-4m-4 0V9a2 2 0 0 0-2-2V5a2 2 0 0 0-2-2 2 2 0 0 0-2 2v2a2 2 0 0 0-2 2v2"/>
					</svg>
				</div>
				<p className="text-sm text-gray-600 dark:text-gray-400 font-medium">No responses found</p>
				<p className="text-xs text-gray-500 dark:text-gray-500 mt-1">Responses will appear here once users interact with your widget</p>
			</div>
		)
	}

	return (
		<>
			<div className={cn('w-full', className)}>
				<Table>
					<TableHeader>
						<TableRow className="border-b border-gray-100 dark:border-gray-800">
							<TableHead className="font-semibold text-gray-700 dark:text-gray-300 py-4">
								Widget
							</TableHead>
							<TableHead className="font-semibold text-gray-700 dark:text-gray-300 py-4">
								Response Summary
							</TableHead>
							<TableHead className="font-semibold text-gray-700 dark:text-gray-300 py-4 text-right">
								Submitted
							</TableHead>
							<TableHead className="font-semibold text-gray-700 dark:text-gray-300 py-4 text-right">
								Device
							</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{responses.map((response) => (
							<TableRow
								key={response.id}
								className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 border-b border-gray-50 dark:border-gray-800/50 transition-colors duration-200"
								onClick={() => setSelectedResponseId(response.id)}>
								<TableCell className="py-4">
									<div className="flex items-center gap-3">
										<Avatar className="h-9 w-9 ring-1 ring-gray-200 dark:ring-gray-700">
											<AvatarFallback className="text-sm font-medium bg-primary/10 text-primary">
												{response.widgets?.name?.[0]?.toUpperCase() || 'W'}
											</AvatarFallback>
										</Avatar>
										<div className="min-w-0">
											<span
												className="font-medium text-gray-900 dark:text-gray-100 block truncate"
												title={response.widgets?.name || 'Unknown Widget'}>
												{response.widgets?.name || 'Unknown Widget'}
											</span>
										</div>
									</div>
								</TableCell>
								<TableCell className="py-4">
									<ResponseSummary response={response} />
								</TableCell>
								<TableCell className="py-4 text-right">
									<time className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
										{formatDistanceToNow(new Date(response.created_at), {
											addSuffix: true,
										})}
									</time>
								</TableCell>
								<TableCell className="py-4 text-right">
									<div className="flex justify-end">
										<DeviceInfo metadata={response.user_metadata} />
									</div>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</div>

			{selectedResponseId && (
				<ResponseDetailView
					responseId={selectedResponseId}
					onClose={() => setSelectedResponseId(null)}
				/>
			)}
		</>
	)
}

export default RecentActivityResponseTable
